import React, { useId, useState, useMemo } from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Card } from '@strivelabs/ui-core';
import { defaultPalette } from '../../lib/chartPalette';
import { ChartLegendContent } from './ChartLegend';
import { ChartWrapper } from './ChartWrapper';
import { calculateTickConfig } from '../../lib/chartTickConfig';
import { truncateLabel, calculateOptimalLabelLength } from '../../lib/chartLabelUtils';
import { renderReferenceLines, renderReferenceDots, renderReferenceAreas, ReferenceDotTooltipLayer, hasReferenceDotDescriptions, useRefDotHovered, resolveRefDotYValues } from './ChartReferences';
import type { ChartReferenceLine, ChartReferenceDot, ChartReferenceArea } from '../../types';

interface DataKey {
  key: string;
  color?: string;
  name?: string;
}

interface BarConfig {
  dataKey: string;
  fill?: string;
  radius?: number[];
}

export interface BarChartProps {
  data: Array<Record<string, string | number>>;
  xAxisKey: string;
  dataKeys?: DataKey[];
  bars?: BarConfig[];
  config?: Record<string, { label?: string; color?: string }>;
  title?: string;
  description?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  yAxisFormatter?: (value: any) => string;
  className?: string;
  /** Enable expand button to view chart in fullscreen dialog */
  expandable?: boolean;
  /** Custom height for the expanded chart in the dialog */
  expandedHeight?: number;
  /** @internal Set by ChartWrapper to indicate expanded view - shows all ticks with longer labels */
  isExpanded?: boolean;
  /** Vertical/horizontal reference lines */
  referenceLines?: ChartReferenceLine[];
  /** Interactive point markers with optional hover tooltips */
  referenceDots?: ChartReferenceDot[];
  /** Shaded reference regions */
  referenceAreas?: ChartReferenceArea[];
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <Card className="p-3 shadow-lg border-border bg-card">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-medium text-foreground">{entry.value}</span>
          </div>
        ))}
      </Card>
    );
  }
  return null;
};

export const BarChart: React.FC<BarChartProps> = ({
  data,
  xAxisKey,
  dataKeys,
  title,
  description,
  height = 400,
  showGrid = true,
  showLegend = true,
  className = '',
  expandable = true,
  expandedHeight = 500,
  isExpanded = false,
  referenceLines: refLines,
  referenceDots: refDots,
  referenceAreas: refAreas,
}) => {
  const [hoveredDataKey, setHoveredDataKey] = useState<string | null>(null);
  const chartId = useId();
  const resolvedRefDots = resolveRefDotYValues(refDots, data, xAxisKey);
  const hasDescriptions = hasReferenceDotDescriptions(resolvedRefDots);
  const refDotHovered = useRefDotHovered(chartId, hasDescriptions);

  // Default colors - violet palette with distinct colors for each data entity
  const defaultColors = defaultPalette;

  // Helper function to check if a key contains numeric values
  const isNumericColumn = (key: string): boolean => {
    return data.some((item) => {
      const value = item?.[key];
      return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)));
    });
  };

  // Auto-generate dataKeys from data if not provided
  // Only includes keys that contain numeric values (safe for bar rendering)
  // Memoized to optimize performance with larger datasets
  const finalDataKeys: DataKey[] = useMemo(() => {
    return dataKeys || (data.length > 0
      ? Object.keys(data[0] ?? {})
          .filter((key) => key !== xAxisKey && isNumericColumn(key))
          .map((key) => ({
            key,
            name: key.charAt(0).toUpperCase() + key.slice(1),
          }))
      : []);
  }, [data, dataKeys, xAxisKey]);

  // Calculate tick label configuration based on data length
  const tickConfig = useMemo(() => {
    // Get base responsive config (handles rotation based on data length)
    const baseConfig = calculateTickConfig(data.length, { top: 20, right: 30, left: 20, bottom: 5 });

    // In expanded view: keep rotation but force showing ALL ticks
    if (isExpanded) {
      const expandedConfig = {
        ...baseConfig,
        interval: 0,  // Force show all ticks (keep rotation from baseConfig)
        margin: {
          ...baseConfig.margin,
          bottom: baseConfig.margin.bottom + 20, // Extra space to prevent legend overlap
        },
      };
      return expandedConfig;
    }

    // Normal view: use responsive logic as-is
    return baseConfig;
  }, [data.length, isExpanded]);

  // Calculate responsive bar size based on number of data points
  // More data points = smaller bars to fit everything nicely
  const maxBarSize = useMemo(() => {
    if (data.length <= 8) return 60;
    if (data.length <= 15) return 45;
    if (data.length <= 30) return 30;
    if (data.length <= 50) return 20;
    return 15;
  }, [data.length]);

  // Create x-axis formatter that truncates labels to prevent overlap
  // In expanded view: 20 chars for better readability, otherwise use responsive calculation
  const xAxisFormatterWithTruncation = useMemo(() => {
    const maxLength = isExpanded ? 20 : calculateOptimalLabelLength(data.length);
    return (value: any) => truncateLabel(value, maxLength);
  }, [data.length, isExpanded]);

  const chartContent = (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart
        data={data}
        margin={tickConfig.margin}
        maxBarSize={maxBarSize}
      >
        {showGrid && (
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="hsl(var(--border))"
            vertical={false}
          />
        )}
        <XAxis
          dataKey={xAxisKey}
          stroke="hsl(var(--muted-foreground))"
          fontSize={tickConfig.fontSize}
          tickLine={false}
          axisLine={true}
          angle={tickConfig.angle}
          textAnchor={tickConfig.textAnchor}
          interval={tickConfig.interval}
          height={tickConfig.margin.bottom}
          tickFormatter={xAxisFormatterWithTruncation}
        />
        <YAxis
          stroke="hsl(var(--muted-foreground))"
          fontSize={12}
          tickLine={false}
          axisLine={true}
          tickFormatter={(value) => `${value}`}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }} wrapperStyle={refDotHovered ? { display: 'none' } : undefined} />
        {showLegend && (
          <Legend
            content={
              <ChartLegendContent
                onMouseEnter={(dataKey) => setHoveredDataKey(dataKey)}
                onMouseLeave={() => setHoveredDataKey(null)}
              />
            }
          />
        )}
        {finalDataKeys.map((item, index) => (
          <Bar
            key={item.key}
            dataKey={item.key}
            fill={item.color || defaultColors[index % defaultColors.length]}
            radius={[4, 4, 0, 0]}
            name={item.name || item.key}
            animationDuration={800}
            opacity={hoveredDataKey === null || hoveredDataKey === item.key ? 1 : 0.3}
          />
        ))}
        {renderReferenceAreas(refAreas)}
        {renderReferenceLines(refLines)}
        {renderReferenceDots(resolvedRefDots, chartId)}
      </RechartsBarChart>
    </ResponsiveContainer>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      className={className}
      expandable={expandable}
      expandedHeight={expandedHeight}
      chartComponent={BarChart}
      chartProps={{
        data,
        xAxisKey,
        dataKeys,
        height,
        showGrid,
        showLegend,
        referenceLines: refLines,
        referenceDots: refDots,
        referenceAreas: refAreas,
        // isExpanded will be added by ChartWrapper in expanded view
      }}
    >
      {chartContent}
      {hasDescriptions && <ReferenceDotTooltipLayer chartId={chartId} />}
    </ChartWrapper>
  );
};
